/**
 * #580 F-08 slice 4b2 — how money kept for later is said, in ONE place, so `/approvals` and the
 * parent Dashboard's pending tile use the same words (4b2 review M1). Held money is never payable
 * and never missing: it is named as kept for later, with its deadline in the family's own zone.
 */

/** Currency formatter that routes NaN/Infinity/"" to a neutral dash — never "$NaN". */
export function formatMoney(value, currency, locale) {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'USD' }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

/**
 * #580 sweep — the family's currency code, or `null` when the server did not state a real one.
 * The same rule Home uses (`dashboardSummaryMapping.js` `CURRENCY_CODE`): no code, no unit.
 */
export function knownCurrency(code) {
  return typeof code === 'string' && /^[A-Za-z]{3}$/.test(code) ? code.toUpperCase() : null;
}

/** An amount with NO unit, for a family whose currency is not known. Never an invented "$". */
export function formatPlainAmount(value, locale) {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  } catch {
    // A malformed locale throws a RangeError. The amount still shows, still with no unit.
    return n.toFixed(2);
  }
}

/** An instant in the family zone, or null when its local date cannot be shown honestly. */
export function formatWhen(iso, timeZone, locale) {
  const at = new Date(iso);
  if (typeof iso !== 'string' || Number.isNaN(at.getTime())) return null;
  if (typeof timeZone !== 'string' || !timeZone) return null;
  try {
    return new Intl.DateTimeFormat(locale, {
      weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone,
    }).format(at);
  } catch {
    return null;
  }
}

/**
 * #580 F-08 slice 4b2 — one line of "Kept for later". It names only the stored deadline, which is
 * right for every payday type (ad hoc has no payday to name). OVERDUE is late and still owed: no
 * settled payout has closed the hold, so nothing here may say it was paid. Failed tries are the
 * release worker's durable record, admitted with the next one.
 */
export function heldSentence(h, currency, locale) {
  const amount = h.total == null ? 'Money' : formatMoney(h.total, currency, locale);
  const when = formatWhen(h.releaseAt, h.timezone, locale);
  let text;
  if (!h.overdue) {
    text = `${amount} for ${h.name} is kept for later. ${when
      ? `It is due to be paid on ${when}.` : 'Its due date is unavailable on this device.'}`;
  } else {
    text = `${amount} for ${h.name} ${when ? `was due on ${when}.` : 'is kept for later. Its due date is unavailable on this device.'} It has not been paid yet — it is late, and it is still owed to ${h.name}.`;
    if (h.attempts > 0) {
      text += ` ${h.attempts === 1 ? 'One try' : `${h.attempts} tries`} to pay it did not go through.`;
      const next = formatWhen(h.nextAttemptAt, h.timezone, locale);
      if (next) text += ` The next try is around ${next}.`;
    }
  }
  // #580 5b (D-5): parent screens only. A talk never changes when the money is paid.
  if (h.talk === 'pending') text += ` A talk with ${h.name} is still to happen. It does not change when the money is paid.`;
  // Not "You": every parent reads this line, and the one reading it may not be the one who marked it (5b review L1).
  if (h.talk === 'resolved') text += ` The talk with ${h.name} has happened.`;
  return text;
}
